package org.teddapp.project.parcours

import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.JsonNull
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.doubleOrNull
import kotlinx.serialization.json.jsonArray
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive

/**
 * Un parcours de compétence lu depuis un fichier JSON, normalisé et vérifié.
 *
 * Deux formes de fichier sont acceptées :
 *
 * - la forme d'origine : `meta.competence`, `ordre` (liste de codes), `paliers`
 *   (par code de projet) et `items` (un objet par code) ;
 * - la forme resserrée : `competence` ou `meta.competence`, puis `parcours`, une
 *   liste ordonnée d'étapes portant chacune son `niveau` et sa `desc`.
 *
 * Une micro s'écrit soit `["titre", minutes, "type"]`, soit `{ "titre", "min", "type" }`.
 * Les minutes des niveaux intermédiaires ne sont pas crues : elles sont recalculées
 * depuis les feuilles, et un écart avec le fichier devient un avertissement.
 */
data class ParcoursPlan(
    /** Identifiant stable du parcours, pour recharger sans doublonner. Null si absent. */
    val slug: String?,
    val competence: String,
    val steps: List<ParcoursStepPlan>,
    val warnings: List<String>,
) {
    val modules: List<ParcoursModulePlan> get() = steps.filterIsInstance<ParcoursModulePlan>()
    val projects: List<ParcoursProjectPlan> get() = steps.filterIsInstance<ParcoursProjectPlan>()
    val totalMinutes: Int get() = steps.sumOf { it.minutes }
    val socleMinutes: Int get() = modules.sumOf { it.minutes }
    val projectMinutes: Int get() = projects.sumOf { it.minutes }
    val objectiveCount: Int get() = modules.sumOf { it.objectives.size }
    val taskCount: Int get() = modules.sumOf { m -> m.objectives.sumOf { it.tasks.size } }
    val microCount: Int get() = modules.sumOf { m -> m.objectives.sumOf { o -> o.tasks.sumOf { it.micros.size } } }
    val actionCount: Int get() = projects.sumOf { it.actions.size }

    /** Minutes par nature de micro (th, pr, at, ev…), dans l'ordre d'apparition. */
    val minutesByKind: Map<String, Int>
        get() {
            val out = linkedMapOf<String, Int>()
            for (m in modules) for (o in m.objectives) for (t in o.tasks) for (mi in t.micros) {
                out[mi.kind] = (out[mi.kind] ?: 0) + mi.minutes
            }
            return out
        }
}

sealed interface ParcoursStepPlan {
    val code: String
    val title: String
    val minutes: Int
}

data class ParcoursModulePlan(
    override val code: String,
    override val title: String,
    val summary: String?,
    val objectives: List<ParcoursObjectivePlan>,
) : ParcoursStepPlan {
    override val minutes: Int get() = objectives.sumOf { it.minutes }
}

data class ParcoursObjectivePlan(
    val code: String,
    val title: String,
    val outcome: String?,
    val tasks: List<ParcoursTaskPlan>,
) {
    val minutes: Int get() = tasks.sumOf { it.minutes }
}

data class ParcoursTaskPlan(
    val code: String,
    val title: String,
    val micros: List<ParcoursMicroPlan>,
) {
    val minutes: Int get() = micros.sumOf { it.minutes }
}

data class ParcoursMicroPlan(val title: String, val minutes: Int, val kind: String)

data class ParcoursProjectPlan(
    override val code: String,
    override val title: String,
    val context: String?,
    val deliverables: List<String>,
    val levelName: String?,
    val levelDesc: String?,
    val actions: List<ParcoursActionPlan>,
) : ParcoursStepPlan {
    override val minutes: Int get() = actions.sumOf { it.minutes }
}

data class ParcoursActionPlan(
    val code: String,
    val title: String,
    val minutes: Int,
    val deliverable: String?,
    val acceptance: String?,
)

/** Ce que donne la lecture d'un fichier : un plan, ou des erreurs qui empêchent d'aller plus loin. */
sealed interface ParcoursParse {
    data class Ok(val plan: ParcoursPlan) : ParcoursParse
    data class Failed(val errors: List<String>) : ParcoursParse
}

/** Natures de micro connues, avec leur libellé à l'écran. */
val PARCOURS_KINDS: Map<String, String> = linkedMapOf(
    "th" to "Théorie",
    "pr" to "Pratique",
    "at" to "Autonomie",
    "ev" to "Évaluation",
)

/** Bornes conseillées d'une micro : au-delà, on avertit sans refuser. */
const val PARCOURS_MICRO_MIN = 5
const val PARCOURS_MICRO_MAX = 20

object ParcoursFile {

    private val json = Json { ignoreUnknownKeys = true; isLenient = true }

    fun parse(text: String): ParcoursParse {
        val root = try {
            json.parseToJsonElement(text).jsonObject
        } catch (e: Exception) {
            return ParcoursParse.Failed(listOf("Le fichier n'est pas un JSON lisible : ${e.message?.lineSequence()?.firstOrNull().orEmpty()}"))
        }
        val errors = mutableListOf<String>()
        val warnings = mutableListOf<String>()

        val meta = root["meta"]?.asObjectOrNull()
        val competence = (root.str("competence") ?: meta?.str("competence"))?.trim().orEmpty()
        if (competence.isEmpty()) errors += "Le nom de la compétence manque (« competence » ou « meta.competence »)."
        val slug = (root.str("slug") ?: meta?.str("slug"))?.trim()?.takeIf { it.isNotEmpty() }

        // Étapes, dans l'ordre : soit la liste « parcours », soit « items » rangés par « ordre ».
        val rawSteps: List<Pair<String, JsonObject>> = when {
            root["parcours"] is JsonArray -> root["parcours"]!!.jsonArray.mapIndexedNotNull { i, el ->
                val obj = el.asObjectOrNull() ?: run { errors += "L'étape n° ${i + 1} n'est pas un objet."; return@mapIndexedNotNull null }
                val code = obj.str("id") ?: obj.str("code") ?: run { errors += "L'étape n° ${i + 1} n'a pas d'identifiant."; return@mapIndexedNotNull null }
                code to obj
            }
            root["items"] is JsonObject -> {
                val items = root["items"]!!.jsonObject
                val order = root["ordre"]?.asArrayOrNull()?.mapNotNull { it.asStringOrNull() } ?: items.keys.toList()
                if (root["ordre"] == null) warnings += "Aucun « ordre » : les étapes suivent l'ordre du fichier."
                val missing = order.filter { it !in items }
                if (missing.isNotEmpty()) errors += "Codes dans « ordre » sans étape correspondante : ${missing.joinToString(", ")}."
                val orphan = items.keys.filter { it !in order }
                if (orphan.isNotEmpty()) warnings += "Étapes absentes de « ordre », ignorées : ${orphan.joinToString(", ")}."
                order.mapNotNull { code -> items[code]?.asObjectOrNull()?.let { code to it } }
            }
            else -> { errors += "Aucune étape : il faut « parcours » (liste) ou « items » + « ordre »."; emptyList() }
        }
        val paliers = root["paliers"]?.asObjectOrNull()

        val seen = mutableSetOf<String>()
        val steps = rawSteps.mapNotNull { (code, obj) ->
            if (!seen.add(code)) { errors += "Le code « $code » apparaît deux fois."; return@mapNotNull null }
            val title = obj.str("titre") ?: obj.str("title") ?: run { errors += "L'étape $code n'a pas de titre."; return@mapNotNull null }
            val explicitKind = obj.str("kind") ?: obj.str("genre")
            val isProject = when (explicitKind) {
                "projet", "project" -> true
                "module" -> false
                else -> obj["actions"] != null || code.startsWith("P")
            }
            if (isProject) parseProject(code, title, obj, paliers?.get(code)?.asObjectOrNull(), errors, warnings)
            else parseModule(code, title, obj, errors, warnings)
        }
        if (steps.isEmpty() && errors.isEmpty()) errors += "Le parcours ne contient aucune étape."

        if (errors.isNotEmpty()) return ParcoursParse.Failed(errors)
        val plan = ParcoursPlan(slug = slug, competence = competence, steps = steps, warnings = warnings)
        checkTotals(plan, meta, warnings)
        return ParcoursParse.Ok(plan.copy(warnings = warnings.toList()))
    }

    private fun parseModule(code: String, title: String, obj: JsonObject, errors: MutableList<String>, warnings: MutableList<String>): ParcoursModulePlan? {
        val objectives = obj["objectifs"]?.asArrayOrNull() ?: run { errors += "Le module $code n'a pas d'« objectifs »."; return null }
        val parsed = objectives.mapIndexedNotNull { i, el ->
            val o = el.asObjectOrNull() ?: run { errors += "$code : l'objectif n° ${i + 1} n'est pas un objet."; return@mapIndexedNotNull null }
            val ocode = o.str("id") ?: "$code.${i + 1}"
            val otitle = o.str("titre") ?: run { errors += "L'objectif $ocode n'a pas de titre."; return@mapIndexedNotNull null }
            val tasks = (o["taches"]?.asArrayOrNull() ?: run { errors += "L'objectif $ocode n'a pas de « taches »."; return@mapIndexedNotNull null })
                .mapIndexedNotNull { j, tel ->
                    val t = tel.asObjectOrNull() ?: run { errors += "$ocode : la tâche n° ${j + 1} n'est pas un objet."; return@mapIndexedNotNull null }
                    val tcode = t.str("id") ?: "$ocode.${j + 1}"
                    val ttitle = t.str("titre") ?: run { errors += "La tâche $tcode n'a pas de titre."; return@mapIndexedNotNull null }
                    val micros = (t["micro"]?.asArrayOrNull() ?: t["micros"]?.asArrayOrNull() ?: run { errors += "La tâche $tcode n'a pas de « micro »."; return@mapIndexedNotNull null })
                        .mapIndexedNotNull { k, mel -> parseMicro(tcode, k, mel, errors, warnings) }
                    val task = ParcoursTaskPlan(tcode, ttitle, micros)
                    checkMinutes("La tâche $tcode", t, task.minutes, warnings)
                    task
                }
            val objective = ParcoursObjectivePlan(ocode, otitle, o.str("resultat") ?: o.str("outcome"), tasks)
            checkMinutes("L'objectif $ocode", o, objective.minutes, warnings)
            objective
        }
        val module = ParcoursModulePlan(code, title, obj.str("resume") ?: obj.str("summary"), parsed)
        checkMinutes("Le module $code", obj, module.minutes, warnings)
        return module
    }

    private fun parseMicro(taskCode: String, index: Int, el: JsonElement, errors: MutableList<String>, warnings: MutableList<String>): ParcoursMicroPlan? {
        val label = "$taskCode, micro n° ${index + 1}"
        val (title, minutes, kind) = when (el) {
            is JsonArray -> Triple(el.getOrNull(0)?.asStringOrNull(), el.getOrNull(1)?.asIntOrNull(), el.getOrNull(2)?.asStringOrNull())
            is JsonObject -> Triple(el.str("titre") ?: el.str("title"), el["min"]?.asIntOrNull() ?: el["minutes"]?.asIntOrNull(), el.str("type") ?: el.str("kind"))
            else -> Triple(null, null, null)
        }
        if (title.isNullOrBlank()) { errors += "$label : titre manquant."; return null }
        if (minutes == null || minutes <= 0) { errors += "$label : durée manquante ou nulle."; return null }
        if (minutes < PARCOURS_MICRO_MIN || minutes > PARCOURS_MICRO_MAX) {
            warnings += "$label : $minutes min, hors de la fourchette $PARCOURS_MICRO_MIN à $PARCOURS_MICRO_MAX min."
        }
        val k = kind?.trim()?.lowercase()?.takeIf { it.isNotEmpty() } ?: "pr"
        if (k !in PARCOURS_KINDS) warnings += "$label : nature « $k » inconnue (attendu : ${PARCOURS_KINDS.keys.joinToString(", ")})."
        return ParcoursMicroPlan(title.trim(), minutes, k)
    }

    private fun parseProject(code: String, title: String, obj: JsonObject, palier: JsonObject?, errors: MutableList<String>, warnings: MutableList<String>): ParcoursProjectPlan? {
        val actions = obj["actions"]?.asArrayOrNull() ?: run { errors += "Le projet $code n'a pas d'« actions »."; return null }
        val parsed = actions.mapIndexedNotNull { i, el ->
            val a = el.asObjectOrNull() ?: run { errors += "$code : l'action n° ${i + 1} n'est pas un objet."; return@mapIndexedNotNull null }
            val acode = a.str("id") ?: "A${code.drop(1)}.${i + 1}"
            val atitle = a.str("titre") ?: run { errors += "L'action $acode n'a pas de titre."; return@mapIndexedNotNull null }
            val min = a["min"]?.asIntOrNull() ?: a["minutes"]?.asIntOrNull() ?: run { errors += "L'action $acode n'a pas de durée."; return@mapIndexedNotNull null }
            ParcoursActionPlan(acode, atitle, min, a.str("livrable") ?: a.str("deliverable"), a.str("critere") ?: a.str("acceptance"))
        }
        val deliverables = obj["livrables"]?.asArrayOrNull()?.mapNotNull { it.asStringOrNull()?.trim()?.takeIf { s -> s.isNotEmpty() } }.orEmpty()
        val levelName = obj.str("niveau") ?: palier?.str("niveau")
        val levelDesc = obj.str("desc") ?: obj.str("niveau_desc") ?: palier?.str("desc")
        if (levelName == null) warnings += "Le projet $code n'annonce pas de palier (« niveau »)."
        val project = ParcoursProjectPlan(code, title, obj.str("contexte") ?: obj.str("context"), deliverables, levelName, levelDesc, parsed)
        checkMinutes("Le projet $code", obj, project.minutes, warnings)
        return project
    }

    /** Les minutes déclarées d'un niveau intermédiaire doivent égaler la somme de ses enfants. */
    private fun checkMinutes(label: String, obj: JsonObject, computed: Int, warnings: MutableList<String>) {
        val declared = obj["min"]?.asIntOrNull() ?: return
        if (declared != computed) warnings += "$label annonce $declared min, ses enfants font $computed min ; on retient $computed."
    }

    private fun checkTotals(plan: ParcoursPlan, meta: JsonObject?, warnings: MutableList<String>) {
        val total = meta?.get("total_min")?.asIntOrNull() ?: return
        if (total != plan.totalMinutes) warnings += "meta.total_min annonce $total min, le parcours en fait ${plan.totalMinutes} ; on retient ${plan.totalMinutes}."
    }

    // ── Petits accès tolérants au JSON ─────────────────────────────────────
    private fun JsonObject.str(key: String): String? = this[key]?.asStringOrNull()?.trim()?.takeIf { it.isNotEmpty() }
    private fun JsonElement.asObjectOrNull(): JsonObject? = this as? JsonObject
    private fun JsonElement.asArrayOrNull(): JsonArray? = this as? JsonArray
    private fun JsonElement.asStringOrNull(): String? = (this as? JsonPrimitive)?.takeIf { it !is JsonNull }?.contentOrNull
    private fun JsonElement.asIntOrNull(): Int? = (this as? JsonPrimitive)?.takeIf { it !is JsonNull }?.let { p ->
        p.doubleOrNull?.toInt() ?: p.contentOrNull?.trim()?.toIntOrNull()
    }
}
